export const isChunkLoadError = (error) => {
  if (!error) return false;
  const message = error.message || '';
  const name = error.name || '';

  return (
    name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  );
};

const extractChunkUrl = (error) => {
  if (error && error.request) return error.request;
  const message = (error && error.message) || '';
  const match = message.match(/(\/?[^\s()]+\.chunk\.(?:js|css))/);
  return match ? match[1] : null;
};

const findChunkInCaches = async (url) => {
  if (!('caches' in window) || !url) return 'unknown';
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const opts = { ignoreSearch: true };
    const names = await caches.keys();
    for (const name of names) {
      const cache = await caches.open(name);
      if (
        (await cache.match(url, opts)) ||
        (await cache.match(pathname, opts))
      ) {
        return 'true';
      }
    }
    return 'false';
  } catch (e) {
    return 'unknown';
  }
};

const MAIN_CHUNK_RE = /\/static\/js\/main\.([a-z0-9]+)\.chunk\.js/i;

const getDocMainHash = () => {
  try {
    const srcs = Array.from(document.querySelectorAll('script[src]')).map(
      (s) => s.getAttribute('src') || '',
    );
    const main = srcs.find((src) => MAIN_CHUNK_RE.test(src));
    const m = main && main.match(MAIN_CHUNK_RE);
    return m ? m[1] : 'unknown';
  } catch (e) {
    return 'unknown';
  }
};

const collectSwDiagnostics = async () => {
  const out = {
    cacheNames: 'unknown',
    precacheCount: 'unknown',
    precacheMainHash: 'unknown',
    swWaiting: 'unknown',
    swInstalling: 'unknown',
  };
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      out.cacheNames = names.join(',') || 'none';
      const precacheName = names.find((n) => n.includes('precache')) || names[0];
      if (precacheName) {
        const reqs = await (await caches.open(precacheName)).keys();
        out.precacheCount = reqs.length;
        const mainReq = reqs.map((r) => r.url).find((u) => MAIN_CHUNK_RE.test(u));
        const m = mainReq && mainReq.match(MAIN_CHUNK_RE);
        out.precacheMainHash = m ? m[1] : 'absent';
      }
    }
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      out.swWaiting = reg && reg.waiting ? 'true' : 'false';
      out.swInstalling = reg && reg.installing ? 'true' : 'false';
    }
  } catch (e) {
    // noop
  }
  return out;
};

const classifyChunkFailure = (chunkInCache, docMainHash, precacheMainHash) => {
  if (chunkInCache === 'true') return 'cache-hit-network-used';
  const known = (h) => h && h !== 'unknown' && h !== 'absent';
  if (known(docMainHash) && known(precacheMainHash)) {
    if (docMainHash !== precacheMainHash) return 'skew';
    if (chunkInCache === 'false') return 'incomplete-install';
    return 'same-build-cache-unknown';
  }
  return 'unknown';
};

export const collectChunkDiagnostics = async (error) => {
  const chunkUrl = extractChunkUrl(error);
  const sw = navigator.serviceWorker;
  const docMainHash = getDocMainHash();

  let chunkInCache = 'unknown';
  let swDiag = {};
  try {
    [chunkInCache, swDiag] = await Promise.all([
      findChunkInCaches(chunkUrl),
      collectSwDiagnostics(),
    ]);
  } catch (e) {
    // noop
  }

  const skewVerdict = classifyChunkFailure(
    chunkInCache,
    docMainHash,
    swDiag.precacheMainHash,
  );

  return {
    tags: {
      chunkError: 'true',
      chunkInCache,
      skewVerdict,
      swControlled: sw && sw.controller ? 'true' : 'false',
      swWaiting: String(swDiag.swWaiting),
      navOnline: navigator.onLine ? 'true' : 'false',
    },
    context: {
      chunkUrl: chunkUrl || 'unknown',
      swScriptUrl: sw && sw.controller ? sw.controller.scriptURL : 'none',
      runningVersion: global.appVersion || 'unknown',
      docMainHash,
      precacheMainHash: swDiag.precacheMainHash,
      precacheCount: swDiag.precacheCount,
      cacheNames: swDiag.cacheNames,
      swWaiting: swDiag.swWaiting,
      swInstalling: swDiag.swInstalling,
      online: navigator.onLine,
    },
  };
};

const SELF_HEAL_KEY = 'msales_chunk_self_heal';
const attemptSelfHeal = () => {
  if (!navigator.onLine) return;
  try {
    if (sessionStorage.getItem(SELF_HEAL_KEY)) return;
    sessionStorage.setItem(SELF_HEAL_KEY, '1');
  } catch (e) {
    return;
  }
  setTimeout(() => {
    window.location.reload();
  }, 1500);
};

let installed = false;
export const installChunkErrorListeners = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    const err = event && (event.error || event);
    if (isChunkLoadError(err)) attemptSelfHeal();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event && event.reason)) attemptSelfHeal();
  });
};
